import { useCallback, useEffect, useMemo, useState } from "react";
import { breakpoints } from "../theme/foundations/breakpoints";

type BreakpointKey = keyof typeof breakpoints | "base";
type BreakpointEntry = [Exclude<BreakpointKey, "base">, string];

/**
 * Use this hook to determine the current breakpoint.
 *
 * It is preferable compared to using Chakra's `useBreakpointValue` hook, as
 * that hook always returns undefined on the first render, which leads to a flicker.
 */
export function useCurrentBreakpoint() {
  const breakpointEntries = useMemo(
    () => getBreakpointEntries(breakpoints),
    [],
  );

  const breakpointOrder = useMemo(
    () => ["base", ...breakpointEntries.map(([key]) => key)] as BreakpointKey[],
    [breakpointEntries],
  );

  const [breakpoint, setBreakpoint] = useState<BreakpointKey>(() =>
    getCurrentBreakpoint(breakpointEntries),
  );

  useEffect(() => {
    const updateBreakpoint = () => {
      setBreakpoint(getCurrentBreakpoint(breakpointEntries));
    };

    updateBreakpoint();

    const mediaQueries = breakpointEntries.map(([, value]) =>
      window.matchMedia(`(min-width: ${value})`),
    );

    mediaQueries.forEach((mediaQuery) => {
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener("change", updateBreakpoint);
        return;
      }

      mediaQuery.addListener(updateBreakpoint);
    });

    return () => {
      mediaQueries.forEach((mediaQuery) => {
        if (mediaQuery.removeEventListener) {
          mediaQuery.removeEventListener("change", updateBreakpoint);
          return;
        }

        mediaQuery.removeListener(updateBreakpoint);
      });
    };
  }, [breakpointEntries]);

  const isAtLeast = useCallback(
    (key: BreakpointKey) =>
      breakpointOrder.indexOf(breakpoint) >= breakpointOrder.indexOf(key),
    [breakpoint, breakpointOrder],
  );

  const isAtMost = useCallback(
    (key: BreakpointKey) =>
      breakpointOrder.indexOf(breakpoint) <= breakpointOrder.indexOf(key),
    [breakpoint, breakpointOrder],
  );

  return {
    breakpoint,
    isAtLeast,
    isAtMost,
  };
}

function getBreakpointEntries(
  breakpoints: Record<string, string>,
): BreakpointEntry[] {
  return Object.entries(breakpoints)
    .filter(([key]) => key !== "base")
    .sort(([, a], [, b]) => parseFloat(a) - parseFloat(b)) as BreakpointEntry[];
}

function getCurrentBreakpoint(
  breakpointEntries: BreakpointEntry[],
): BreakpointKey {
  if (typeof window === "undefined") {
    return "base";
  }

  let current: BreakpointKey = "base";

  for (const [key, value] of breakpointEntries) {
    if (window.matchMedia(`(min-width: ${value})`).matches) {
      current = key;
    }
  }

  return current;
}
